import React, { useState, useEffect, useRef, } from 'react';
import {
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    InputAdornment,
    Grid,
    Container,
    styled,
    CircularProgress,
    Autocomplete,
    TablePagination,
    IconButton,
    Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { fetchResident } from "../../Components/Masters/Billing/Api/Api";
import { getRoomMasterdropdata } from '../../Api/PropertyMst/Api';
import { toast } from 'react-toastify';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import axios from 'axios';
import logo from '../../assets/Images/Clanbridge-logo.png2.png';
import 'dayjs/locale/en-gb';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginTop: theme.spacing(3),
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.common.white,
    fontWeight: 'bold',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: '7px 24px',
    borderRadius: '8px',
    textTransform: 'none',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
}));

const FilterSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
    marginBottom: theme.spacing(3),
}));

const StatusBadge = styled(Box)(({ theme, status }) => ({
    display: 'inline-block',
    px: 1.5,
    py: 0.5,
    borderRadius: '12px',
    fontWeight: '500',
    fontSize: '0.75rem',
    backgroundColor:
        status === 'Completed' ? theme.palette.success.light :
            status === 'Cancelled' ? theme.palette.error.light :
                theme.palette.warning.light,
    color:
        status === 'Completed' ? theme.palette.success.dark :
            status === 'Cancelled' ? theme.palette.error.dark :
                theme.palette.warning.dark,
}));

const BookingReport = () => {
    const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
    const [toDate, setToDate] = useState(dayjs());
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedRoomType, setSelectedRoomType] = useState(null);
    const [selectedResident, setSelectedResident] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [roomTypes, setRoomTypes] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('All');
    const [properties, setProperties] = useState([{ Id: 0, Name: "All" }]);
    const [residents, setResidents] = useState([{ Id: 0, Name: "All" }]);
    const navigate = useNavigate()
    const token = `eyJhbGciOiJIUzI1NiJ9.eyJWYWxpZElzc3VlciI6IlByYXN5c3QiLCJWYWxpZEF1ZGllbmNlIjoiUHJhc3lzdCJ9.gMMGQ74cWLoymeDv0D4c3UQKTEJSCzdkXoxlW4qF5QU`;

    const fetchPropertyData = async () => {
        try {
            const userId = sessionStorage.getItem('UserId');
            const response = await getRoomMasterdropdata({
                Flag: '',
                UserId: userId,
            });

            if (response.data.Status === 0) {
                setProperties([{ Id: 0, Name: "All" }, ...response.data.Data]);
            } else {
                console.warn('Error fetching property data:', response.data.Message);
                toast.error(response.data.Message || 'Error fetching property data');
            }
        } catch (error) {
            console.error('Error fetching property data:', error);
            toast.error('Failed to fetch property data');
        }
    };


    const fetchResidentData = async () => {
        try {
            const response = await fetchResident({
                start: 1,
                PageSize: 1,
                SearchText: "drp",
            });

            if (response?.data?.Data) {
                setResidents([{ Id: 0, Name: "All" }, ...response.data.Data]);
            } else {
                toast.error("No residents data found");
            }
        } catch (error) {
            console.error("Error fetching residents:", error);
            toast.error("Failed to load residents data");
        }
    };

    const fetchBookingReports = async () => {
        try {
            setTableLoading(true);
            const payload = {
                FromDate: fromDate.format('YYYY-MM-DD'),
                ToDate: toDate.format('YYYY-MM-DD'),
                PropId: selectedProperty?.Id?.toString(),
                RoomTypeId: selectedRoomType?.Id?.toString(),
                ResidentId: selectedResident?.Id?.toString()
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}BookingReports/GetBookingReportsDetail`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.Status === 0) {
                setData(response.data.Data);
                setFilteredData(response.data.Data);
            } else {
                toast.error(response.data.Message || "No data found");
                setData([]);
                setFilteredData([]);
            }
        } catch (error) {
            console.error("Error fetching booking reports:", error);
            toast.error("Failed to load booking reports");
            setData([]);
            setFilteredData([]);
        } finally {
            setTableLoading(false);
        }
    };

    const getStatus = (booking) => {
        return booking.BookSt === 'B' ? 'Booked' :
            booking.BookSt === 'C' ? 'Confirmed' :
                booking.BookSt === 'O' ? 'Occupied' :
                    'Cancelled';
    };

    const filterByStatus = (status) => {
        setStatusFilter(status);
        if (status === 'All') {
            setFilteredData(data);
        } else {
            const statusMap = {
                'Booked': 'B',
                'Confirmed': 'C',
                'Occupied': 'O',
                'Cancelled': 'X'
            };
            setFilteredData(data.filter(item => item.BookSt === statusMap[status]));
        }
        setPage(0);
    };


    useEffect(() => {
        fetchPropertyData();
        fetchResidentData();
    }, []);

    const handlePropertyChange = (event, newValue) => {
        setSelectedProperty(newValue?.Id === 0 ? null : newValue);
    };


    const handleResidentChange = (event, newValue) => {
        setSelectedResident(newValue?.Id === 0 ? null : newValue);

    };

    const handleReset = () => {
        setSelectedProperty(null);
        setSelectedRoomType(null);
        setSelectedResident(null);
        setFromDate(dayjs().subtract(1, 'month'));
        setToDate(dayjs());
        setData([]);
        setFilteredData([]);
        setStatusFilter('All');
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedData = filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    console.log('paginatedData', paginatedData)
    console.log('filteredData', filteredData)
    const tableRef = useRef();

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const currentDate = new Date().toLocaleString();
        const logoHtml = logo ? `<img src="${logo}" style="height: 35px; margin-bottom: 10px;" alt="logo" />` : '';
        const tableHtml = `
            <html>
                <head>
                    <title>Booking Report</title>
                    <style>
                        @media print {
                            body {
                                font-family: Arial, sans-serif;
                                margin: 0;
                                padding: 10px;
                            }
                            .print-header {
                                text-align: center;
                                margin-bottom: 15px;
                            }
                            .print-title {
                                font-size: 18px;
                                font-weight: bold;
                                margin-bottom: 5px;
                            }
                            .print-date {
                                font-size: 12px;
                                margin-bottom: 10px;
                            }
                            .print-filters {
                                font-size: 12px;
                                margin-bottom: 15px;
                            }
                            table {
                                width: 100%;
                                border-collapse: collapse;
                                font-size: 12px;
                            }
                            th, td {
                                border: 1px solid #ddd;
                                padding: 4px 6px;
                                text-align: left;
                            }
                            th {
                                background-color: #1976d2;
                                color: white;
                                font-weight: bold;
                            }
                            .status-badge {
                                display: inline-block;
                                padding: 2px 6px;
                                border-radius: 4px;
                                font-weight: 500;
                                font-size: 11px;
                                color: white;
                            }
                            @page {
                                size: auto;
                                margin: 5mm;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-header">
                        ${logoHtml}
                        <div class="print-title">Booking Report</div>
                        <div class="print-date">Generated on: ${new Date().toLocaleString()}</div>
                        <div class="print-filters">
                            ${selectedProperty ? `Property: ${selectedProperty.Name} | ` : ''}
                            ${selectedRoomType ? `Room Type: ${selectedRoomType.Name} | ` : ''}
                            ${selectedResident ? `Resident: ${selectedResident.Name} | ` : ''}
                            Date Range: ${fromDate.format('DD/MM/YYYY')} to ${toDate.format('DD/MM/YYYY')} |
                            Status: ${statusFilter}
                        </div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Booking No</th>
                                <th>Booking Date</th>
                                <th>Check-in Date</th>
                             
                                 <th>Name</th>
                                    <th>Gender</th>
                                <th>Mobile</th>
                                   <th>Duration (months)</th>
                                <th>Total Amount</th>
                                <th>Deposit</th>
                                 
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${paginatedData.map(row => `
                                <tr>
                                    <td>${row.BookNo}</td>
                                    <td>${dayjs(row.BookDt).format('DD/MM/YYYY')}</td>
                                    <td>${dayjs(row.CheckinDt).format('DD/MM/YYYY')}</td>
                                      <td>${row.Name}</td>
                                    <td>${row.Gender}</td>
                                    <td>${row.Mobile}</td>
                                    <td>${row.Duration}</td>
                                    <td>${row.TotalRent}</td>
                                    <td>${row.DepositeAmt}</td>
                                   
                                    <td>
                                        <span class="status-badge" style="background-color: ${getStatus(row) === 'Booked' ? '#2196f3' :
                getStatus(row) === 'Confirmed' ? '#4caf50' :
                    getStatus(row) === 'Occupied' ? '#ff9800' :
                        '#f44336'
            }">
    ${getStatus(row)}
</span>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div style="margin-top: 10px; font-size: 11px;">
                        Total Records: ${filteredData?.length} | Page ${page + 1} of ${Math.ceil(filteredData.length / rowsPerPage)}
                    </div>
                </body>
            </html>
        `;

        printWindow.document.write(tableHtml);
        printWindow.document.close();

        // Wait for content to load before printing
        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 200);
        };
    };
     const handleBackClick = () => {
        navigate('/reports/bookdetails')
    }

    return (
        <Container maxWidth="lg" sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" gutterBottom sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                }}>
                    <IconButton
                                            onClick={handleBackClick}
                                            sx={{
                                                left: '-5px',
                                                color: '#6a11cb',
                    
                                                backgroundColor: 'rgba(106, 17, 203, 0.1)'
                    
                                            }}
                                        >
                                            <KeyboardBackspaceIcon />
                                        </IconButton>
                    Booking Report
                </Typography>

                <Tooltip title="Print Report">
                    <IconButton
                        onClick={handlePrint}
                        disabled={filteredData?.length === 0}
                        color="primary"
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            }
                        }}
                    >
                        <PrintIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <FilterSection>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                inputFormat="DD/MM/YYYY"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EventIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                inputFormat="DD/MM/YYYY"
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EventIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Property Autocomplete */}
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            options={properties}
                            getOptionLabel={(option) => option?.Name || ''}
                            value={selectedProperty || { Id: 0, Name: "All" }}
                            onChange={handlePropertyChange}
                            isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Property"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: {
                                            p: 0,
                                            height: '42px',
                                            '& input': {
                                                padding: '8.5px 8px',
                                            },
                                        },
                                    }}

                                />
                            )}
                        />
                    </Grid>

                    {/* Resident Autocomplete */}
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            options={residents}
                            getOptionLabel={(option) => option?.Name || ''}
                            value={selectedResident || { Id: 0, Name: "All" }}
                            onChange={handleResidentChange}
                            isOptionEqualToValue={(option, value) => option?.Id === value?.Id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Resident"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: {
                                            p: 0,
                                            height: '42px',
                                            '& input': {
                                                padding: '8.5px 8px',
                                            },
                                        },
                                    }}

                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 2 }}>
                        <StyledButton
                            variant="contained"
                            color="primary"
                            onClick={fetchBookingReports}
                            fullWidth
                            disabled={tableLoading}
                        >
                            {tableLoading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
                        </StyledButton>
                        <StyledButton
                            variant="outlined"
                            color="primary"
                            onClick={handleReset}
                            fullWidth
                            disabled={tableLoading}
                        >
                            Reset
                        </StyledButton>
                    </Grid>
                </Grid>
            </FilterSection>

            <Box sx={{
                display: 'flex',
                gap: 1,
                my: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: 0
            }}>
                <Button
                    size="small"
                    variant={statusFilter === 'All' ? 'contained' : 'outlined'}
                    onClick={() => filterByStatus('All')}
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: 0.5,
                        ...(statusFilter === 'All' && {
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                        })
                    }}
                >
                    All
                </Button>
                <Button
                    size="small"
                    variant={statusFilter === 'Booked' ? 'contained' : 'outlined'}
                    color="info"
                    onClick={() => filterByStatus('Booked')}
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: 0.5,
                        ...(statusFilter === 'Booked' && {
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                        })
                    }}
                >
                    Booked
                </Button>
                <Button
                    size="small"
                    variant={statusFilter === 'Confirmed' ? 'contained' : 'outlined'}
                    color="success"
                    onClick={() => filterByStatus('Confirmed')}
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: 0.5,
                        ...(statusFilter === 'Confirmed' && {
                            boxShadow: '0 1px 4px rgba(76,175,80,0.2)'
                        })
                    }}
                >
                    Confirmed
                </Button>
                <Button
                    size="small"
                    variant={statusFilter === 'Occupied' ? 'contained' : 'outlined'}
                    color="warning"
                    onClick={() => filterByStatus('Occupied')}
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: 0.5,
                        ...(statusFilter === 'Occupied' && {
                            boxShadow: '0 1px 4px rgba(255,167,38,0.2)'
                        })
                    }}
                >
                    Occupied
                </Button>
                <Button
                    size="small"
                    variant={statusFilter === 'Cancelled' ? 'contained' : 'outlined'}
                    color="error"
                    onClick={() => filterByStatus('Cancelled')}
                    sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        borderRadius: 0.5,
                        ...(statusFilter === 'Cancelled' && {
                            boxShadow: '0 1px 4px rgba(244,67,54,0.2)'
                        })
                    }}
                >
                    Cancelled
                </Button>
            </Box>

            <StyledTableContainer component={Paper} ref={tableRef}>
                <Table sx={{ minWidth: 650 }} size="small">
                    <StyledTableHead>
                        <TableRow sx={{ '& th': { py: 0.5 } }}>
                            <StyledTableCell>Booking No</StyledTableCell>
                            <StyledTableCell>Booking Date</StyledTableCell>
                            <StyledTableCell>Check-in Date</StyledTableCell>
                            <StyledTableCell>Mobile</StyledTableCell>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Gender</StyledTableCell>
                            <StyledTableCell>Duration (months)</StyledTableCell>
                            <StyledTableCell>Total Amount</StyledTableCell>
                            <StyledTableCell>Deposit</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                        </TableRow>
                    </StyledTableHead>
                    <TableBody>
                        {paginatedData?.length > 0 ? (
                            paginatedData.map((row) => (
                                <TableRow
                                    key={row.BookId}
                                    hover
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '& td': { py: 0.25 }
                                    }}
                                >
                                    <TableCell>{row.BookNo}</TableCell>
                                    <TableCell>{dayjs(row.BookDt).format('DD/MM/YYYY')}</TableCell>
                                    <TableCell>{dayjs(row.CheckinDt).format('DD/MM/YYYY')}</TableCell>
                                    <TableCell>{row.Mobile}</TableCell>
                                    <TableCell>{row.Name}</TableCell>
                                    <TableCell>{row.Gender}</TableCell>
                                    <TableCell>{row.Duration}</TableCell>
                                    <TableCell>{row.TotalRent}</TableCell>
                                    <TableCell>{row.DepositeAmt}</TableCell>

                                    <TableCell>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                color: 'white',
                                                fontWeight: 500,
                                                fontSize: '0.7rem',
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: '2px',
                                                minWidth: '80px',
                                                justifyContent: 'center',
                                                textTransform: 'capitalize',
                                                backgroundColor:
                                                    getStatus(row) === 'Booked' ? 'info.main' :
                                                        getStatus(row) === 'Confirmed' ? 'success.main' :
                                                            getStatus(row) === 'Occupied' ? 'warning.main' :
                                                                'error.main'
                                            }}
                                        >
                                            {getStatus(row)}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow sx={{ '& td': { py: 1 } }}>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {tableLoading ? 'Loading data...' : 'No records found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredData?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ py: 0.5 }}
                />
            </StyledTableContainer>
        </Container>
    );
};

export default BookingReport;